/**
 * ShareViewDialog.jsx - "Share…" on one of the person's own table views
 * (round 4, 10/9/26: "share view to other users as well and it pops up on
 * their views"). Pick people from the GlobalOps roster (searchable, several
 * at once) and / or Everyone; it shows who has it now. Save sends the whole
 * list (PUT /api/table-views/:id/shares); "Stop sharing" empties it. The
 * people picked get the view read-only under "Shared with me", and a notice.
 *
 * The roster is AppContext's users - on GLOBAL KSA, only the people who can
 * open KSA. The server checks every email is a GlobalOps user.
 */
import { useMemo, useState } from 'react';
import { Modal, Button } from '../ui';
import { useApp } from '../../context/AppContext';

export default function ShareViewDialog({ view, target, onClose }) {
  const { users } = useApp() || {};
  const was = target.sharedWith || [];
  const [picked, setPicked] = useState(() => new Set(was.filter((e) => e !== '*')));
  const [everyone, setEveryone] = useState(was.includes('*'));
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const me = String(target.owner || '').toLowerCase();

  const roster = useMemo(
    () =>
      (users || [])
        .filter((u) => u && u.email && u.email.toLowerCase() !== me && u.role !== 'pending')
        .map((u) => ({ email: u.email.toLowerCase(), name: u.name || u.email }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users, me]
  );
  const nameOf = (e) => (roster.find((u) => u.email === e) || {}).name || e;
  const shown = roster.filter((u) => !q || `${u.name} ${u.email}`.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (e) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(e)) n.delete(e);
      else n.add(e);
      return n;
    });

  const save = async (emails, all) => {
    setBusy(true);
    const ok = await view.share(target.id, emails, all);
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Modal
      title={`Share “${target.name}”`}
      size="md"
      onClose={onClose}
      footer={
        <>
          {was.length > 0 && (
            <Button variant="ghost" disabled={busy} onClick={() => save([], false)}>
              Stop sharing
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={() => save([...picked], everyone)}>
            Save sharing
          </Button>
        </>
      }
    >
      <p className="text-[12px] text-text-muted mt-0 mb-3">
        The people you pick see this view under <b>Shared with me</b> - read only, always your latest version.
      </p>
      <div className="text-[12px] mb-3" data-testid="shared-now">
        {was.length ? (
          <>
            Shared now with <b>{was.includes('*') ? 'everyone' : was.map(nameOf).join(', ')}</b>.
          </>
        ) : (
          'Not shared yet.'
        )}
      </div>
      <label className="flex items-center gap-2 text-[12.5px] font-semibold mb-2">
        <input type="checkbox" checked={everyone} onChange={(e) => setEveryone(e.target.checked)} />
        Everyone in GlobalOps
      </label>
      <input
        aria-label="Search people"
        placeholder="Search people…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="w-full border border-border-strong rounded px-2 py-1.5 text-[12px] mb-2"
      />
      <ul
        className="m-0 p-0 list-none border border-border rounded-md divide-y divide-border max-h-[260px] overflow-auto"
        aria-label="People"
      >
        {shown.map((u) => (
          <li key={u.email} className="px-2.5 py-1.5 text-[12px]">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={picked.has(u.email)} onChange={() => toggle(u.email)} aria-label={`Share with ${u.name}`} />
              <span className="flex-1">
                {u.name} <span className="text-text-muted">{u.email}</span>
              </span>
            </label>
          </li>
        ))}
        {!shown.length && <li className="px-2.5 py-2 text-[12px] text-text-muted">Nobody matches.</li>}
      </ul>
      {picked.size > 0 && <div className="text-[11.5px] text-text-muted mt-2">{picked.size} picked</div>}
      {view.error && (
        <div className="text-[11.5px] text-status-lost mt-2" role="alert">
          {view.error}
        </div>
      )}
    </Modal>
  );
}
